import { Box, Button, Input } from '@mui/material'
import { INode } from 'shared/types/tree'
import React from 'react'
import { useGetTreeFromURL } from '../TreeActions'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { GoogleLoginButton, googleUserAtom } from 'infrastructure/googleDrive'
import { GDriveSpreadsheet } from '../googleApis/types'
import {
  addSectionOverviews,
  deleteSectionOverviews,
  parseSpreadsheet,
  sectionOverview,
} from './utilities'
import styled from '@emotion/styled'

export function SectionOverviewsForVA() {
  const [sheetId, setSheetId] = React.useState<string>('')
  const [spreadsheet, setSpreadsheet] = React.useState<GDriveSpreadsheet>()

  const [fetchingSpreadsheet, setFetchingSpreadsheet] = React.useState(false)

  const [gradesToUnitsToOverviews, setGradesToUnitsToOverviews] =
    React.useState<Record<string, Record<number, sectionOverview[]>>>({})

  const guser = useAtomValue(googleUserAtom)

  React.useEffect(() => {
    if (guser.credential?.accessToken && sheetId) {
      setFetchingSpreadsheet(true)
      GDriveSpreadsheet.create(guser.credential?.accessToken, sheetId).then(
        (s) => {
          setSpreadsheet(s)
          setFetchingSpreadsheet(false)
          setGradesToUnitsToOverviews(parseSpreadsheet(s))
        },
      )
    }
  }, [sheetId, guser.credential?.accessToken])

  if (!guser.credential?.accessToken) {
    return (
      <Box>
        <GoogleLoginButton hideWhenLoggedIn={true} />
      </Box>
    )
  }

  return (
    <Box>
      <Input
        placeholder="Enter Sheet ID"
        onChange={(e) => setSheetId(e.target.value)}
      />
      {fetchingSpreadsheet && <Box>Fetching Spreadsheet...</Box>}
      {Object.entries(gradesToUnitsToOverviews).map(
        ([grade, unitsToOverviews]) => {
          return (
            <CustomBox key={grade}>
              <GradeView grade={grade} overviews={unitsToOverviews} />
            </CustomBox>
          )
        },
      )}
    </Box>
  )
}

function GradeView({
  grade,
  overviews,
}: {
  grade: string
  overviews: Record<number, sectionOverview[]>
}) {
  const getTreeFromURL = useGetTreeFromURL()
  const [treeId, setTreeId] = React.useState<string>('')
  const [tree, setTree] = React.useState<INode>()
  const [deleting, setDeleting] = React.useState(false)
  const [adding, setAdding] = React.useState(false)

  React.useEffect(() => {
    if (treeId) {
      getTreeFromURL(treeId).then(setTree)
    }
  }, [treeId])

  if (!tree) {
    return (
      <Box>
        {grade}
        <Input
          onChange={(e) => setTreeId(e.target.value)}
          placeholder="Enter Tree ID"
        />
      </Box>
    )
  }

  return (
    <Box>
      {grade} -<b>{tree.title}</b>
      <Box>
        <Button
          disabled={deleting}
          onClick={async () => {
            setDeleting(true)
            await deleteSectionOverviews(tree)
            setTree(await getTreeFromURL(treeId))
            setDeleting(false)
          }}
        >
          {deleting
            ? 'Deleting Existing Section Overviews...'
            : 'Delete Existing Section Overviews'}
        </Button>
        <Button
          disabled={adding}
          onClick={async () => {
            setAdding(true)
            await addSectionOverviews(tree, overviews)
            setTree(await getTreeFromURL(treeId))
            setAdding(false)
          }}
        >
          {adding
            ? 'Adding New Section Overviews...'
            : 'Add New Section Overviews'}
        </Button>
      </Box>
    </Box>
  )
}

const CustomBox = styled(Box)(() => ({
  width: '80%',
  padding: '10px',
  margin: '10px',
  border: '1px solid black',
}))
